import {
  CheckboxController,
  Collapse,
  NumberController,
  Text,
} from "@pushpress/shared-components";
import React, { useRef } from "react";
import { Control, useWatch } from "react-hook-form"; // eslint-disable-line
import { PlanForm } from "../PlanDrawer.types";
import { useExperiment } from "../../../../hooks/useExperiment";
import { PlanType } from "src/apis/subscriptionApi";

interface OtherSectionProps {
  control: Control<PlanForm>;
  planType: PlanType;
}

const OtherSection = (props: OtherSectionProps) => {
  const { control, planType } = props;

  const [billEveryInterval] = useWatch({
    control,
    name: ["billEveryInterval"],
  });

  const { useExperimentValue } = useExperiment();

  const priorityBookingFeatureFlag = useExperimentValue(
    "priority_booking_feature_flag",
    false
  );

  const perPersonLimitFeatureFlag = useExperimentValue(
    "plan_per_person_limit",
    false
  );

  const fixedBillingDayOfMonthFeatureFlag = useExperimentValue(
    "plan-prorate-landing-pages",
    false
  );

  const forcePurchaseStartDateFeatureFlag = useExperimentValue(
    "plan-custom-start-date-landing-pages",
    false
  );

  const quantityLimitRef = useRef<HTMLDivElement>(null);
  const priorityBookingRef = useRef<HTMLDivElement>(null);
  const perPersonLimitRef = useRef<HTMLDivElement>(null);
  const hasFixedBillingDayOfMonthRef = useRef<HTMLDivElement>(null);
  
  const [hasLimit] = useWatch({
    control,
    name: ["hasLimit"],
  });

  const handleHasLimitToggle = (value: unknown) => {
    if (value) {
      // it needs to wait the collapse to animate to scroll the element into the view
      setTimeout(() => {
        quantityLimitRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  };

  const [priorityBooking] = useWatch({
    control,
    name: ["priorityBooking"],
  });

  const handlePriorityMinutesToggle = (value: unknown) => {
    if (value) {
      // it needs to wait the collapse to animate to scroll the element into the view
      setTimeout(() => {
        priorityBookingRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  };

  const [perPersonLimit] = useWatch({
    control,
    name: ["perPersonLimit"],
  });

  const handlePerPersonLimitToggle = (value: unknown) => {
    if (value) {
      // it needs to wait the collapse to animate to scroll the element into the view
      setTimeout(() => {
        perPersonLimitRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  };


  const [hasFixedBillingDayOfMonth] = useWatch({
    control,
    name: ["hasFixedBillingDayOfMonth"],
  });
  
  const handleFixedBillingDOMToggle = (value: unknown) => {
    if (value) {
      // it needs to wait the collapse to animate to scroll the element into the view
      setTimeout(() => {
        hasFixedBillingDayOfMonthRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  };

  return (
    <div>
      <Text
        component="h2"
        variant="heading-5"
        weight="600"
        color="wolf-800"
        className="mt-4"
      >
        Other settings
      </Text>
      <Text variant="body-md" color="wolf-600" className="mt-1">
        Configure other options about this plan
      </Text>
      <CheckboxController
        control={control}
        name="hybridAf"
        label="HybridAF (24-hr access)"
        className="mt-4"
      />
      <CheckboxController
        control={control}
        name="hasLimit"
        label="Limit quantity"
        className="mt-3"
        onToggle={handleHasLimitToggle}
      />
      <Text variant="body-md" color="wolf-600" className="mt-1">
        Enable this option if you want to sell this plan a specific number of
        times before it becomes unavailable.
      </Text>
      <Collapse in={hasLimit} ref={quantityLimitRef}>
        <NumberController
          control={control}
          name="quantityAvailable"
          label="Quantity limit"
          className="mt-4"
          required
        />
      </Collapse>
      {priorityBookingFeatureFlag && (
        <div>
          <CheckboxController
            control={control}
            name="priorityBooking"
            label="Priority Booking"
            className="mt-3"
            onToggle={handlePriorityMinutesToggle}
          />
          <Text variant="body-md" color="wolf-600" className="mt-1">
            Enable this option if you want clients with this plan to be able to book
            prior to the class reservation window.
          </Text>
          <Collapse in={priorityBooking} ref={priorityBookingRef}>
            <NumberController
              control={control}
              name="priorityHoursInAdvance"
              label="Hours in Advance"
              className="mt-4"
              required
            />
          </Collapse>
        </div>
      )}
      {perPersonLimitFeatureFlag && (
        <div>
          <CheckboxController
            control={control}
            name="perPersonLimit"
            label="Limit Per Person"
            className="mt-3"
            onToggle={handlePerPersonLimitToggle}
          />
          <Text variant="body-md" color="wolf-600" className="mt-1">
          Enable this option if a client should only be able to purchase this plan a specific number of times.
          </Text>
          <Collapse in={perPersonLimit} ref={perPersonLimitRef}>
            <NumberController
              control={control}
              name="perPersonLimitNum"
              label="Per person limit"
              min={1}
              max={100}
              className="mt-4"
              required
            />
          </Collapse>
        </div>
      )}
      {forcePurchaseStartDateFeatureFlag && (
        <div>
          <CheckboxController
            control={control}
            name="forcePurchaseStartDate"
            label="Force Purchase Start Date"
            className="mt-3"
          />
          <Text variant="body-md" color="wolf-600" className="mt-1">
          Enable this option if a client is forced to start their plan immediately upon purchase.
          </Text>
        </div>
      )}
      {planType == "recurring" && billEveryInterval?.value == 'month' && fixedBillingDayOfMonthFeatureFlag && (
        <div>
          <CheckboxController
            control={control}
            name="hasFixedBillingDayOfMonth"
            label="Fixed Billing Day of Month"
            className="mt-3"
            onToggle={handleFixedBillingDOMToggle}
          />
          <Text variant="body-md" color="wolf-600" className="mt-1">
          Align all subscriptions to a fixed billing day each month. Existing subscriptions will be adjusted with a prorated charge to match the billing schedule.
          </Text>
          <Collapse in={hasFixedBillingDayOfMonth} ref={hasFixedBillingDayOfMonthRef}>
            <NumberController
              control={control}
              name="fixedBillingDayOfMonth"
              label="Billing day of month"
              min={1}
              max={31}
              className="mt-4"
              required
            />
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default OtherSection;
